/**
 * @file Level assembly and teardown.
 *
 * Sits between the loading state and the individual factories so that
 * "what a run consists of" is described in one readable place.
 * Generation only happens here (via Loading); never while PLAYING.
 */

import { Arena, levelSeed } from './Arena.js';
import { createPlayer } from '../entities/Player.js';
import { Transform, Weapon } from '../components/index.js';
import { WaveSystem } from '../systems/WaveSystem.js';
import { ParticleSystem } from '../systems/ParticleSystem.js';
import { randomStartingWeapon } from '../config/Weapons.js';

/**
 * Builds a fresh level: dungeon, player at entrance (A), exit at boss (B).
 *
 * @param {import('../core/Context.js').GameContext} ctx Shared game context.
 * @returns {boolean} false if dungeon generation failed
 */
export function buildLevel(ctx) {
  teardownLevel(ctx);

  if (ctx.runSeed == null) {
    ctx.runSeed = (Math.random() * 0xffffffff) >>> 0;
  }
  if (ctx.levelIndex == null || ctx.levelIndex < 1) {
    ctx.levelIndex = 1;
  }
  ctx.levelSeed = levelSeed(ctx.runSeed, ctx.levelIndex);

  const arena = new Arena(ctx);
  const ok = arena.build();
  if (!ok) {
    arena.dispose();
    return false;
  }
  ctx.arena = arena;

  const { x, z } = arena.entrance;
  ctx.player = createPlayer(ctx, x, z);

  ctx.world.getSystem(WaveSystem)?.reset();

  // Snap rather than glide: the camera must not fly in from the last run.
  ctx.cameraController.snapTo(ctx.player.get(Transform).position);
  ctx.lighting.focusOn(ctx.player.get(Transform).position);

  return true;
}

/**
 * Advance to the next dungeon floor (A→B). Caller transitions to LOADING.
 * @param {import('../core/Context.js').GameContext} ctx
 * @returns {void}
 */
export function prepareNextLevel(ctx) {
  if (ctx.player) {
    ctx.playerWeaponKey = ctx.player.get(Weapon)?.key ?? null;
  }
  ctx.levelIndex = (ctx.levelIndex ?? 1) + 1;
  ctx.levelSeed = levelSeed(ctx.runSeed ?? 1, ctx.levelIndex);
}

/**
 * Start a brand-new run from the menu.
 * @param {import('../core/Context.js').GameContext} ctx
 * @returns {void}
 */
export function prepareNewRun(ctx) {
  ctx.runSeed = (Math.random() * 0xffffffff) >>> 0;
  ctx.levelIndex = 1;
  ctx.levelSeed = levelSeed(ctx.runSeed, 1);
  ctx.playerWeaponKey = randomStartingWeapon();
}

/**
 * Destroys the current run, releasing entities, meshes and effects.
 *
 * @param {import('../core/Context.js').GameContext} ctx Shared game context.
 * @returns {void}
 */
export function teardownLevel(ctx) {
  ctx.world.getSystem(ParticleSystem)?.clear();

  if (ctx.arena) {
    ctx.arena.dispose();
    ctx.arena = null;
  }

  ctx.world.clear();
  ctx.player = null;
}
